import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../components/ui/dropdown-menu";
import { 
  Check, Mail, Mic, Sparkles, Zap, Shield, Globe, ArrowRight, Menu, X, ChevronDown, 
  Inbox, Bot, MessageSquare, Send, Star, Tag, Sun, Moon,
  Brain, Calendar, BarChart3, Bell, Lightbulb, FileText, TrendingUp, Clock,
  CalendarClock, Filter, Download, ChevronRight
} from "lucide-react";
import { useLanguage, SUPPORTED_LANGUAGES } from "../i18n/LanguageProvider";
import { useTheme } from "../components/ThemeProvider";
import TelcaVoIPBadge from "../components/TelcaVoIPBadge";

const COMPANY_INFO = {
  name: "TelcaVoIP International EU",
  address: "Ul. Zbozowa 4A/2, 70-653 Szczecin, Poland",
  vat: "PL8513242072",
  email: "info@telcavoip.eu",
  support: "support@telcavoip.eu",
  whatsapp: "+48 573 187804"
};

const LandingPage = () => {
  const navigate = useNavigate();
  const { t, language, setLanguage } = useLanguage();
  const { theme, toggleTheme } = useTheme();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [billingCycle, setBillingCycle] = useState("monthly");

  const PLANS = [
    {
      id: "free",
      name: language === 'it' ? "Free" : "Free",
      price: "0",
      period: language === 'it' ? "per sempre" : "forever",
      features: language === 'it' 
        ? ["1 account email", "50 richieste AI/mese", "Categorizzazione base", "Supporto community"]
        : ["1 email account", "50 AI requests/month", "Basic categorization", "Community support"],
      cta: language === 'it' ? "Inizia Gratis" : "Start Free",
      popular: false
    },
    {
      id: "pro",
      name: "Pro",
      price: billingCycle === "monthly" ? "9.99" : "99.99",
      period: billingCycle === "monthly" ? (language === 'it' ? "/mese" : "/month") : (language === 'it' ? "/anno" : "/year"),
      features: language === 'it' 
        ? ["5 account email", "500 richieste AI/mese", "Comandi vocali", "Text-to-Speech", "Supporto prioritario", "14 giorni di prova GRATIS"]
        : ["5 email accounts", "500 AI requests/month", "Voice commands", "Text-to-Speech", "Priority support", "14-day FREE trial"],
      cta: language === 'it' ? "Prova Pro" : "Try Pro",
      popular: true
    },
    {
      id: "enterprise",
      name: "Enterprise",
      price: billingCycle === "monthly" ? "29.99" : "299.99",
      period: billingCycle === "monthly" ? (language === 'it' ? "/mese" : "/month") : (language === 'it' ? "/anno" : "/year"),
      features: language === 'it' 
        ? ["Account illimitati", "AI illimitata", "Accesso API", "Integrazioni custom", "Supporto dedicato 24/7"]
        : ["Unlimited accounts", "Unlimited AI", "API access", "Custom integrations", "Dedicated support 24/7"],
      cta: language === 'it' ? "Contattaci" : "Contact Us",
      popular: false
    }
  ];

  const currentLang = SUPPORTED_LANGUAGES.find(l => l.code === language);

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass-panel">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center">
                <Mail className="w-5 h-5 text-primary-foreground" />
              </div>
              <span className="text-xl font-bold tracking-tight">TelcaVoIP</span>
            </Link>

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-muted-foreground hover:text-foreground transition-colors">{t('nav.features')}</a>
              <a href="#pricing" className="text-muted-foreground hover:text-foreground transition-colors">{t('nav.pricing')}</a>
              <Link to="/login" className="text-muted-foreground hover:text-foreground transition-colors">{t('nav.login')}</Link>
              
              {/* Theme Toggle */}
              <Button 
                variant="ghost" 
                size="icon"
                onClick={toggleTheme}
                data-testid="theme-toggle"
              >
                {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </Button>
              
              {/* Language Selector */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="gap-2">
                    <span>{currentLang?.flag}</span>
                    <span className="hidden sm:inline">{currentLang?.code.toUpperCase()}</span>
                    <ChevronDown className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {SUPPORTED_LANGUAGES.map((lang) => (
                    <DropdownMenuItem 
                      key={lang.code}
                      onClick={() => setLanguage(lang.code)}
                      className="gap-2"
                    >
                      <span>{lang.flag}</span>
                      <span>{lang.name}</span>
                      {language === lang.code && <Check className="w-4 h-4 ml-auto" />}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              <Button 
                data-testid="get-started-nav-btn"
                onClick={() => navigate("/register")}
                className="glow-primary-hover"
              >
                {t('nav.getStarted')}
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button 
              className="md:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-border bg-background/95 backdrop-blur-xl">
            <div className="px-4 py-4 space-y-4">
              <a href="#features" className="block text-muted-foreground hover:text-foreground">{t('nav.features')}</a>
              <a href="#pricing" className="block text-muted-foreground hover:text-foreground">{t('nav.pricing')}</a>
              <Link to="/login" className="block text-muted-foreground hover:text-foreground">{t('nav.login')}</Link>
              <div className="flex gap-2 flex-wrap">
                {SUPPORTED_LANGUAGES.map((lang) => (
                  <Button 
                    key={lang.code}
                    variant={language === lang.code ? "default" : "outline"}
                    size="sm"
                    onClick={() => setLanguage(lang.code)}
                  >
                    {lang.flag}
                  </Button>
                ))}
              </div>
              <Button onClick={() => navigate("/register")} className="w-full">{t('nav.getStarted')}</Button>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-24 md:pb-32 hero-gradient overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            <div className="lg:col-span-6 space-y-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-primary/20 to-purple-500/20 border border-primary/30 text-sm">
                <Sparkles className="w-4 h-4 text-primary animate-pulse" />
                <span className="font-semibold bg-gradient-to-r from-primary to-purple-500 bg-clip-text text-transparent">AI Mail</span>
                <span className="text-muted-foreground">•</span>
                <span className="text-primary">{t('hero.badge')}</span>
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-tight">
                {t('hero.title')}
                <span className="text-primary">{t('hero.titleHighlight')}</span>
              </h1>
              
              <p className="text-lg text-muted-foreground max-w-xl">
                {t('hero.subtitle')}
              </p>
              
              {/* New AI Features Pills */}
              <div className="flex flex-wrap gap-2">
                <span className="px-3 py-1 rounded-full bg-blue-500/10 text-blue-600 text-xs font-medium border border-blue-500/20">
                  <Brain className="w-3 h-3 inline mr-1" />
                  {language === 'it' ? 'Suggerimenti AI' : 'AI Suggestions'}
                </span>
                <span className="px-3 py-1 rounded-full bg-green-500/10 text-green-600 text-xs font-medium border border-green-500/20">
                  <Calendar className="w-3 h-3 inline mr-1" />
                  {language === 'it' ? 'Rileva Meeting' : 'Meeting Detection'}
                </span>
                <span className="px-3 py-1 rounded-full bg-purple-500/10 text-purple-600 text-xs font-medium border border-purple-500/20">
                  <BarChart3 className="w-3 h-3 inline mr-1" />
                  {language === 'it' ? 'Analytics Avanzate' : 'Advanced Analytics'}
                </span>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  data-testid="hero-cta-btn"
                  size="lg" 
                  onClick={() => navigate("/register")}
                  className="glow-primary text-lg px-8 py-6"
                >
                  {t('hero.cta')} <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <Button 
                  data-testid="hero-demo-btn"
                  variant="outline" 
                  size="lg"
                  onClick={() => navigate("/login")}
                  className="text-lg px-8 py-6"
                >
                  {t('hero.demo')}
                </Button>
              </div>

              <div className="flex items-center gap-6 text-sm text-muted-foreground">
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-500" />
                  <span>{t('hero.noCard')}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-500" />
                  <span>{t('hero.setup')}</span>
                </div>
              </div>
            </div>

            {/* Hero Visual - Email Interface Mockup */}
            <div className="lg:col-span-6 relative">
              <div className="relative rounded-2xl overflow-hidden border border-border/50 shadow-2xl bg-card p-1">
                {/* Mockup Email Interface */}
                <div className="rounded-xl bg-background overflow-hidden">
                  {/* Mockup Header */}
                  <div className="h-10 bg-card/80 border-b border-border/50 flex items-center px-4 gap-2">
                    <div className="w-3 h-3 rounded-full bg-destructive/60" />
                    <div className="w-3 h-3 rounded-full bg-yellow-500/60" />
                    <div className="w-3 h-3 rounded-full bg-green-500/60" />
                    <span className="ml-4 text-xs text-muted-foreground">TelcaVoIP AI Mail</span>
                  </div>
                  
                  {/* Mockup Content */}
                  <div className="flex h-64">
                    {/* Sidebar */}
                    <div className="w-16 bg-card/50 border-r border-border/30 p-2 space-y-2">
                      <div className="w-8 h-8 mx-auto rounded-lg bg-primary/20 flex items-center justify-center">
                        <Inbox className="w-4 h-4 text-primary" />
                      </div>
                      <div className="w-8 h-8 mx-auto rounded-lg bg-secondary/50 flex items-center justify-center">
                        <Star className="w-4 h-4 text-muted-foreground" />
                      </div>
                      <div className="w-8 h-8 mx-auto rounded-lg bg-secondary/50 flex items-center justify-center">
                        <Send className="w-4 h-4 text-muted-foreground" />
                      </div>
                      <div className="w-8 h-8 mx-auto rounded-lg bg-secondary/50 flex items-center justify-center">
                        <Tag className="w-4 h-4 text-muted-foreground" />
                      </div>
                    </div>
                    
                    {/* Email List */}
                    <div className="w-32 bg-card/30 border-r border-border/30 p-2 space-y-2">
                      <div className="p-2 rounded bg-primary/10 border-l-2 border-primary">
                        <div className="w-16 h-2 bg-foreground/60 rounded mb-1" />
                        <div className="w-12 h-1.5 bg-muted-foreground/40 rounded" />
                      </div>
                      <div className="p-2 rounded bg-secondary/30">
                        <div className="w-14 h-2 bg-foreground/40 rounded mb-1" />
                        <div className="w-10 h-1.5 bg-muted-foreground/30 rounded" />
                      </div>
                      <div className="p-2 rounded bg-secondary/30">
                        <div className="w-12 h-2 bg-foreground/40 rounded mb-1" />
                        <div className="w-8 h-1.5 bg-muted-foreground/30 rounded" />
                      </div>
                    </div>
                    
                    {/* Email Content + AI */}
                    <div className="flex-1 p-3 space-y-3">
                      {/* Email Header */}
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="w-24 h-3 bg-foreground/60 rounded mb-1" />
                          <div className="w-16 h-2 bg-muted-foreground/40 rounded" />
                        </div>
                        <div className="flex gap-1">
                          <span className="px-2 py-0.5 text-[10px] rounded-full bg-amber-500/20 text-amber-500">urgent</span>
                        </div>
                      </div>
                      
                      {/* AI Summary Box */}
                      <div className="p-2 rounded-lg bg-primary/5 border border-primary/20">
                        <div className="flex items-center gap-1 mb-1">
                          <Sparkles className="w-3 h-3 text-primary" />
                          <span className="text-[10px] text-primary font-medium">AI Summary</span>
                        </div>
                        <div className="space-y-1">
                          <div className="w-full h-1.5 bg-primary/20 rounded" />
                          <div className="w-3/4 h-1.5 bg-primary/20 rounded" />
                        </div>
                      </div>
                      
                      {/* Email Body */}
                      <div className="space-y-1">
                        <div className="w-full h-1.5 bg-muted-foreground/20 rounded" />
                        <div className="w-full h-1.5 bg-muted-foreground/20 rounded" />
                        <div className="w-2/3 h-1.5 bg-muted-foreground/20 rounded" />
                      </div>
                      
                      {/* AI Reply Button */}
                      <div className="flex gap-2">
                        <div className="px-3 py-1.5 rounded bg-primary text-[10px] text-primary-foreground flex items-center gap-1">
                          <Bot className="w-3 h-3" />
                          AI Reply
                        </div>
                        <div className="px-3 py-1.5 rounded bg-secondary text-[10px] text-muted-foreground flex items-center gap-1">
                          <Mic className="w-3 h-3" />
                          Voice
                        </div>
                      </div>
                    </div>
                    
                    {/* AI Chat Panel */}
                    <div className="w-28 bg-card/50 border-l border-border/30 p-2">
                      <div className="flex items-center gap-1 mb-2">
                        <MessageSquare className="w-3 h-3 text-primary" />
                        <span className="text-[10px] font-medium">AI Chat</span>
                      </div>
                      <div className="space-y-1.5">
                        <div className="p-1.5 rounded bg-secondary/50 text-[8px] text-muted-foreground">
                          <div className="w-full h-1 bg-muted-foreground/30 rounded mb-0.5" />
                          <div className="w-2/3 h-1 bg-muted-foreground/30 rounded" />
                        </div>
                        <div className="p-1.5 rounded bg-primary/10 ml-2 text-[8px]">
                          <div className="w-full h-1 bg-primary/30 rounded mb-0.5" />
                          <div className="w-3/4 h-1 bg-primary/30 rounded" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Floating badge */}
              <div className="absolute -bottom-4 -left-4 px-4 py-2 rounded-lg bg-card border border-border shadow-lg flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center">
                  <Check className="w-4 h-4 text-green-500" />
                </div>
                <div>
                  <p className="text-xs font-medium">{language === 'it' ? 'Email analizzata' : 'Email analyzed'}</p>
                  <p className="text-[10px] text-muted-foreground">{language === 'it' ? 'Categoria: Urgente' : 'Category: Urgent'}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* NEW: AI Features Showcase */}
      <section className="py-20 md:py-28 bg-gradient-to-b from-primary/5 to-transparent">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 text-sm mb-6">
              <Brain className="w-4 h-4 text-primary" />
              <span className="text-primary font-medium">{language === 'it' ? 'Intelligenza Artificiale Avanzata' : 'Advanced Artificial Intelligence'}</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">
              {language === 'it' ? 'L\'AI che lavora per te' : 'AI that works for you'}
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {language === 'it' 
                ? 'Funzionalità intelligenti che automatizzano la gestione delle tue email'
                : 'Smart features that automate your email management'}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
            {/* AI Rule Suggestions */}
            <div className="group relative p-8 rounded-2xl bg-gradient-to-br from-blue-500/10 to-blue-600/5 border border-blue-500/20 hover:border-blue-500/40 transition-all duration-300 hover:-translate-y-1 overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-blue-500/10 rounded-full blur-3xl" />
              <div className="relative">
                <div className="w-14 h-14 rounded-xl bg-blue-500/20 flex items-center justify-center mb-6">
                  <Lightbulb className="w-7 h-7 text-blue-500" />
                </div>
                <h3 className="text-xl font-semibold mb-3">
                  {language === 'it' ? 'Suggerimenti Regole AI' : 'AI Rule Suggestions'}
                </h3>
                <p className="text-muted-foreground mb-4">
                  {language === 'it' 
                    ? 'L\'AI analizza i tuoi pattern email e suggerisce regole automatiche per organizzare la posta. Mittenti frequenti, subject ricorrenti, tutto viene rilevato.'
                    : 'AI analyzes your email patterns and suggests automatic rules to organize your mail. Frequent senders, recurring subjects, everything is detected.'}
                </p>
                <div className="flex items-center gap-2 text-blue-500 text-sm font-medium">
                  <Filter className="w-4 h-4" />
                  <span>{language === 'it' ? 'Regole intelligenti' : 'Smart rules'}</span>
                </div>
              </div>
            </div>

            {/* Meeting Detection */}
            <div className="group relative p-8 rounded-2xl bg-gradient-to-br from-green-500/10 to-green-600/5 border border-green-500/20 hover:border-green-500/40 transition-all duration-300 hover:-translate-y-1 overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-green-500/10 rounded-full blur-3xl" />
              <div className="relative">
                <div className="w-14 h-14 rounded-xl bg-green-500/20 flex items-center justify-center mb-6">
                  <CalendarClock className="w-7 h-7 text-green-500" />
                </div>
                <h3 className="text-xl font-semibold mb-3">
                  {language === 'it' ? 'Rilevamento Meeting' : 'Meeting Detection'}
                </h3>
                <p className="text-muted-foreground mb-4">
                  {language === 'it' 
                    ? 'L\'AI scansiona le email per date e orari di meeting. Rileva "domani alle 15", "lunedì 10 febbraio" e ti permette di aggiungerli al calendario con un click.'
                    : 'AI scans emails for meeting dates and times. Detects "tomorrow at 3pm", "Monday February 10" and lets you add them to calendar with one click.'}
                </p>
                <div className="flex items-center gap-2 text-green-500 text-sm font-medium">
                  <Calendar className="w-4 h-4" />
                  <span>{language === 'it' ? 'Calendario integrato' : 'Integrated calendar'}</span>
                </div>
              </div>
            </div>

            {/* Advanced Analytics */}
            <div className="group relative p-8 rounded-2xl bg-gradient-to-br from-purple-500/10 to-purple-600/5 border border-purple-500/20 hover:border-purple-500/40 transition-all duration-300 hover:-translate-y-1 overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-purple-500/10 rounded-full blur-3xl" />
              <div className="relative">
                <div className="w-14 h-14 rounded-xl bg-purple-500/20 flex items-center justify-center mb-6">
                  <BarChart3 className="w-7 h-7 text-purple-500" />
                </div>
                <h3 className="text-xl font-semibold mb-3">
                  {language === 'it' ? 'Analytics Avanzate' : 'Advanced Analytics'}
                </h3>
                <p className="text-muted-foreground mb-4">
                  {language === 'it' 
                    ? 'Grafici interattivi con confronto automatico tra periodi. Visualizza trend, seleziona intervalli ed esporta report in PDF o CSV.'
                    : 'Interactive charts with automatic period comparison. Visualize trends, select ranges and export reports to PDF or CSV.'}
                </p>
                <div className="flex items-center gap-4 text-purple-500 text-sm font-medium">
                  <div className="flex items-center gap-1">
                    <TrendingUp className="w-4 h-4" />
                    <span>{language === 'it' ? 'Confronto periodi' : 'Period comparison'}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Download className="w-4 h-4" />
                    <span>PDF/CSV</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Smart Notifications */}
            <div className="group relative p-8 rounded-2xl bg-gradient-to-br from-orange-500/10 to-orange-600/5 border border-orange-500/20 hover:border-orange-500/40 transition-all duration-300 hover:-translate-y-1 overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-orange-500/10 rounded-full blur-3xl" />
              <div className="relative">
                <div className="w-14 h-14 rounded-xl bg-orange-500/20 flex items-center justify-center mb-6">
                  <Bell className="w-7 h-7 text-orange-500" />
                </div>
                <h3 className="text-xl font-semibold mb-3">
                  {language === 'it' ? 'Notifiche Intelligenti' : 'Smart Notifications'}
                </h3>
                <p className="text-muted-foreground mb-4">
                  {language === 'it' 
                    ? 'Ricevi avvisi per meeting urgenti rilevati nelle prossime 48 ore. Badge colorati indicano l\'urgenza: oggi, domani, dopodomani.'
                    : 'Get alerts for urgent meetings detected in the next 48 hours. Colored badges indicate urgency: today, tomorrow, day after.'}
                </p>
                <div className="flex items-center gap-3 text-sm">
                  <span className="px-2 py-1 rounded bg-red-500/20 text-red-500 text-xs font-medium">
                    {language === 'it' ? 'Oggi' : 'Today'}
                  </span>
                  <span className="px-2 py-1 rounded bg-orange-500/20 text-orange-500 text-xs font-medium">
                    {language === 'it' ? 'Domani' : 'Tomorrow'}
                  </span>
                  <span className="px-2 py-1 rounded bg-yellow-500/20 text-yellow-600 text-xs font-medium">
                    {language === 'it' ? 'Dopodomani' : 'Day after'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* NEW: How It Works */}
      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">
              {language === 'it' ? 'Come Funziona' : 'How It Works'}
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {language === 'it' 
                ? 'Tre semplici passi per trasformare la gestione delle tue email'
                : 'Three simple steps to transform your email management'}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Step 1 */}
            <div className="relative text-center">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6 relative">
                <span className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-primary text-primary-foreground text-sm font-bold flex items-center justify-center">1</span>
                <Mail className="w-8 h-8 text-primary" />
              </div>
              <h3 className="text-xl font-semibold mb-3">
                {language === 'it' ? 'Connetti la tua Email' : 'Connect Your Email'}
              </h3>
              <p className="text-muted-foreground">
                {language === 'it' 
                  ? 'Collega Gmail, Outlook o qualsiasi account IMAP in pochi secondi.'
                  : 'Connect Gmail, Outlook or any IMAP account in seconds.'}
              </p>
              {/* Arrow */}
              <div className="hidden md:block absolute top-8 right-0 translate-x-1/2">
                <ChevronRight className="w-8 h-8 text-muted-foreground/30" />
              </div>
            </div>

            {/* Step 2 */}
            <div className="relative text-center">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6 relative">
                <span className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-primary text-primary-foreground text-sm font-bold flex items-center justify-center">2</span>
                <Brain className="w-8 h-8 text-primary" />
              </div>
              <h3 className="text-xl font-semibold mb-3">
                {language === 'it' ? 'L\'AI Analizza' : 'AI Analyzes'}
              </h3>
              <p className="text-muted-foreground">
                {language === 'it' 
                  ? 'GPT-5.2 categorizza, rileva meeting, suggerisce regole e genera risposte.'
                  : 'GPT-5.2 categorizes, detects meetings, suggests rules and generates replies.'}
              </p>
              {/* Arrow */}
              <div className="hidden md:block absolute top-8 right-0 translate-x-1/2">
                <ChevronRight className="w-8 h-8 text-muted-foreground/30" />
              </div>
            </div>

            {/* Step 3 */}
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6 relative">
                <span className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-primary text-primary-foreground text-sm font-bold flex items-center justify-center">3</span>
                <Zap className="w-8 h-8 text-primary" />
              </div>
              <h3 className="text-xl font-semibold mb-3">
                {language === 'it' ? 'Gestisci con Intelligenza' : 'Manage Smartly'}
              </h3>
              <p className="text-muted-foreground">
                {language === 'it' 
                  ? 'Rispondi con un click, crea eventi, applica regole. Tutto automatizzato.'
                  : 'Reply with one click, create events, apply rules. All automated.'}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* NEW: Stats Section */}
      <section className="py-16 bg-gradient-to-r from-primary/5 via-primary/10 to-primary/5">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-primary mb-2">500+</div>
              <p className="text-sm text-muted-foreground">
                {language === 'it' ? 'Richieste AI / giorno' : 'AI requests / day'}
              </p>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-green-500 mb-2">98%</div>
              <p className="text-sm text-muted-foreground">
                {language === 'it' ? 'Accuratezza categorizzazione' : 'Categorization accuracy'}
              </p>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-purple-500 mb-2">10x</div>
              <p className="text-sm text-muted-foreground">
                {language === 'it' ? 'Più veloce nella gestione' : 'Faster management'}
              </p>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-orange-500 mb-2">24/7</div>
              <p className="text-sm text-muted-foreground">
                {language === 'it' ? 'AI sempre attiva' : 'AI always active'}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">
              {t('features.title')}
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {t('features.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* AI Categorization */}
            <div className="group p-8 rounded-lg bg-card/50 border border-border/50 hover:border-primary/30 transition-all duration-300 hover:-translate-y-1">
              <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-6">
                <Sparkles className="w-6 h-6 text-primary" />
              </div>
              <h3 className="text-xl font-semibold mb-3">{t('features.categorization.title')}</h3>
              <p className="text-muted-foreground">
                {t('features.categorization.description')}
              </p>
            </div>

            {/* Voice Commands */}
            <div className="group p-8 rounded-lg bg-card/50 border border-border/50 hover:border-primary/30 transition-all duration-300 hover:-translate-y-1">
              <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-6">
                <Mic className="w-6 h-6 text-primary" />
              </div>
              <h3 className="text-xl font-semibold mb-3">{t('features.voice.title')}</h3>
              <p className="text-muted-foreground">
                {t('features.voice.description')}
              </p>
            </div>

            {/* Smart Replies */}
            <div className="group p-8 rounded-lg bg-card/50 border border-border/50 hover:border-primary/30 transition-all duration-300 hover:-translate-y-1">
              <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-6">
                <Zap className="w-6 h-6 text-primary" />
              </div>
              <h3 className="text-xl font-semibold mb-3">{t('features.replies.title')}</h3>
              <p className="text-muted-foreground">
                {t('features.replies.description')}
              </p>
            </div>

            {/* Text to Speech */}
            <div className="group p-8 rounded-lg bg-card/50 border border-border/50 hover:border-primary/30 transition-all duration-300 hover:-translate-y-1">
              <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-6">
                <Globe className="w-6 h-6 text-primary" />
              </div>
              <h3 className="text-xl font-semibold mb-3">{t('features.tts.title')}</h3>
              <p className="text-muted-foreground">
                {t('features.tts.description')}
              </p>
            </div>

            {/* AI Chat */}
            <div className="group p-8 rounded-lg bg-card/50 border border-border/50 hover:border-primary/30 transition-all duration-300 hover:-translate-y-1">
              <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-6">
                <Mail className="w-6 h-6 text-primary" />
              </div>
              <h3 className="text-xl font-semibold mb-3">{t('features.chat.title')}</h3>
              <p className="text-muted-foreground">
                {t('features.chat.description')}
              </p>
            </div>

            {/* Security */}
            <div className="group p-8 rounded-lg bg-card/50 border border-border/50 hover:border-primary/30 transition-all duration-300 hover:-translate-y-1">
              <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-6">
                <Shield className="w-6 h-6 text-primary" />
              </div>
              <h3 className="text-xl font-semibold mb-3">{t('features.security.title')}</h3>
              <p className="text-muted-foreground">
                {t('features.security.description')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-24 md:py-32 bg-card/30">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">
              {t('pricing.title')}
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {t('pricing.subtitle')}
            </p>
          </div>

          {/* Billing Cycle Toggle */}
          <div className="flex items-center justify-center gap-4 mb-12">
            <span className={billingCycle === "monthly" ? "text-foreground" : "text-muted-foreground"}>
              {t('pricing.monthly')}
            </span>
            <button
              onClick={() => setBillingCycle(billingCycle === "monthly" ? "yearly" : "monthly")}
              className={`relative w-14 h-7 rounded-full transition-colors ${
                billingCycle === "yearly" ? "bg-primary" : "bg-secondary"
              }`}
            >
              <div
                className={`absolute top-1 w-5 h-5 rounded-full bg-white transition-transform ${
                  billingCycle === "yearly" ? "translate-x-8" : "translate-x-1"
                }`}
              />
            </button>
            <span className={billingCycle === "yearly" ? "text-foreground" : "text-muted-foreground"}>
              {t('pricing.yearly')}
            </span>
            {billingCycle === "yearly" && (
              <span className="px-2 py-1 text-xs bg-green-500/20 text-green-500 rounded-full">
                {t('pricing.save')}
              </span>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {PLANS.map((plan) => (
              <div 
                key={plan.id}
                data-testid={`pricing-card-${plan.id}`}
                className={`relative p-8 rounded-lg border transition-all duration-300 hover:-translate-y-1 ${
                  plan.popular 
                    ? "bg-primary/5 border-primary/50 glow-primary" 
                    : "bg-card/50 border-border/50 hover:border-primary/30"
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-primary text-primary-foreground text-sm font-medium rounded-full">
                    {t('pricing.popular')}
                  </div>
                )}
                
                <h3 className="text-2xl font-bold mb-2">{plan.name}</h3>
                <div className="mb-6">
                  <span className="text-4xl font-bold">€{plan.price}</span>
                  <span className="text-muted-foreground">{plan.period}</span>
                </div>

                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-3">
                      <Check className="w-5 h-5 text-green-500 flex-shrink-0" />
                      <span className="text-muted-foreground">{feature}</span>
                    </li>
                  ))}
                </ul>

                <Button 
                  data-testid={`pricing-cta-${plan.id}`}
                  onClick={() => navigate("/register")}
                  variant={plan.popular ? "default" : "outline"}
                  className={`w-full ${plan.popular ? "glow-primary-hover" : ""}`}
                >
                  {plan.cta}
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="relative rounded-2xl overflow-hidden p-12 md:p-16 text-center">
            <div className="absolute inset-0 bg-gradient-to-r from-primary/20 via-accent/20 to-primary/20" />
            <div className="absolute inset-0 hero-gradient" />
            
            <div className="relative z-10">
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">
                {t('cta.title')}
              </h2>
              <p className="text-lg text-muted-foreground max-w-xl mx-auto mb-8">
                {t('cta.subtitle')}
              </p>
              <Button 
                data-testid="final-cta-btn"
                size="lg" 
                onClick={() => navigate("/register")}
                className="glow-primary text-lg px-8 py-6"
              >
                {t('cta.button')} <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* NEW: Final CTA Section */}
      <section className="py-20 md:py-28 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-purple-500/10 to-blue-500/20" />
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/20 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl" />
        
        <div className="relative max-w-4xl mx-auto px-4 md:px-8 lg:px-12 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-background/80 backdrop-blur border border-border text-sm mb-6">
            <Sparkles className="w-4 h-4 text-primary animate-pulse" />
            <span className="font-medium">{language === 'it' ? 'Inizia oggi, è gratis' : 'Start today, it\'s free'}</span>
          </div>
          
          <h2 className="text-3xl md:text-5xl font-bold tracking-tight mb-6">
            {language === 'it' 
              ? 'Pronto a rivoluzionare la tua email?'
              : 'Ready to revolutionize your email?'}
          </h2>
          
          <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
            {language === 'it' 
              ? 'Unisciti a migliaia di utenti che gestiscono le email in modo intelligente con AI Mail. Nessuna carta di credito richiesta.'
              : 'Join thousands of users managing emails smartly with AI Mail. No credit card required.'}
          </p>
          
          {/* Feature highlights */}
          <div className="flex flex-wrap justify-center gap-4 mb-10">
            <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-background/60 backdrop-blur border border-border">
              <Brain className="w-5 h-5 text-blue-500" />
              <span className="text-sm">{language === 'it' ? 'Suggerimenti AI' : 'AI Suggestions'}</span>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-background/60 backdrop-blur border border-border">
              <CalendarClock className="w-5 h-5 text-green-500" />
              <span className="text-sm">{language === 'it' ? 'Rileva Meeting' : 'Meeting Detection'}</span>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-background/60 backdrop-blur border border-border">
              <BarChart3 className="w-5 h-5 text-purple-500" />
              <span className="text-sm">{language === 'it' ? 'Analytics' : 'Analytics'}</span>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-background/60 backdrop-blur border border-border">
              <Bell className="w-5 h-5 text-orange-500" />
              <span className="text-sm">{language === 'it' ? 'Notifiche Smart' : 'Smart Notifications'}</span>
            </div>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              onClick={() => navigate("/register")}
              className="glow-primary text-lg px-10 py-6"
              data-testid="final-cta-btn"
            >
              {language === 'it' ? 'Prova AI Mail Gratis' : 'Try AI Mail Free'}
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button 
              variant="outline" 
              size="lg"
              onClick={() => navigate("/login")}
              className="text-lg px-10 py-6 bg-background/60 backdrop-blur"
            >
              {language === 'it' ? 'Ho già un account' : 'I have an account'}
            </Button>
          </div>
          
          <p className="mt-6 text-sm text-muted-foreground">
            <Check className="w-4 h-4 inline mr-1 text-green-500" />
            {language === 'it' ? 'Setup in 2 minuti' : 'Setup in 2 minutes'}
            <span className="mx-3">•</span>
            <Check className="w-4 h-4 inline mr-1 text-green-500" />
            {language === 'it' ? 'Nessuna carta richiesta' : 'No card required'}
            <span className="mx-3">•</span>
            <Check className="w-4 h-4 inline mr-1 text-green-500" />
            {language === 'it' ? 'Cancella quando vuoi' : 'Cancel anytime'}
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-border">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            {/* Brand */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
                  <Mail className="w-4 h-4 text-primary-foreground" />
                </div>
                <span className="font-bold">TelcaVoIP AI Mail</span>
              </div>
              <p className="text-sm text-muted-foreground">
                {language === 'it' 
                  ? 'Gestisci la tua email con l\'intelligenza artificiale.'
                  : 'Manage your email with artificial intelligence.'}
              </p>
            </div>

            {/* Product */}
            <div>
              <h4 className="font-semibold mb-4">{language === 'it' ? 'Prodotto' : 'Product'}</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><a href="#features" className="hover:text-foreground transition-colors">{t('nav.features')}</a></li>
                <li><a href="#pricing" className="hover:text-foreground transition-colors">{t('nav.pricing')}</a></li>
                <li><Link to="/login" className="hover:text-foreground transition-colors">{t('nav.login')}</Link></li>
              </ul>
            </div>

            {/* Legal */}
            <div>
              <h4 className="font-semibold mb-4">{language === 'it' ? 'Legale' : 'Legal'}</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><Link to="/privacy" className="hover:text-foreground transition-colors">{t('footer.privacy')}</Link></li>
                <li><Link to="/terms" className="hover:text-foreground transition-colors">{t('footer.terms')}</Link></li>
                <li><Link to="/cookies" className="hover:text-foreground transition-colors">{t('footer.cookies')}</Link></li>
                <li><Link to="/withdrawal" className="hover:text-foreground transition-colors">{t('footer.withdrawal')}</Link></li>
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h4 className="font-semibold mb-4">{t('nav.contact')}</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li>{COMPANY_INFO.email}</li>
                <li>{COMPANY_INFO.support}</li>
                <li>WhatsApp: {COMPANY_INFO.whatsapp}</li>
                <li>{COMPANY_INFO.address}</li>
                <li>VAT: {COMPANY_INFO.vat}</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col md:flex-row items-center justify-between gap-4 pt-8 border-t border-border">
            <p className="text-sm text-muted-foreground">
              © 2024 {COMPANY_INFO.name}. {language === 'it' ? 'Tutti i diritti riservati.' : 'All rights reserved.'}
            </p>

            <div className="flex items-center gap-4">
              {SUPPORTED_LANGUAGES.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => setLanguage(lang.code)}
                  className={`text-sm ${language === lang.code ? 'text-primary' : 'text-muted-foreground hover:text-foreground'} transition-colors`}
                >
                  {lang.flag} {lang.code.toUpperCase()}
                </button>
              ))}
            </div>
          </div>
          
          {/* Badge */}
          <div className="mt-8 pt-8 border-t border-border flex justify-center">
            <TelcaVoIPBadge />
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
